import { useEffect, useRef, useState } from 'react'
import type { SavedPatch } from './useEditorActions'
import { rebaseArticleForm, type ArticleForm } from '@/features/instructions/lib/articleDraft'

const sameValues = (a: ArticleForm, b: ArticleForm) => JSON.stringify(a) === JSON.stringify(b)

/**
 * Форма статьи поверх засева. Новый засев вливается `rebaseArticleForm` против «базы
 * авторства»: для полей, ушедших в последний `PATCH`, база — форма на момент клика (откат
 * во время полёта остаётся авторским, нормализованное сервером — нет), для остальных —
 * прежний засев. Засев, совпавший по значениям с прежним (перечитали статью, черновик ещё
 * не приехал), базу не съедает: засев приходит двумя шагами. Съедает её только ИТОГОВЫЙ —
 * оба запроса ушли не раньше ответа `PATCH` (`seedReadAt >= savedAt`): так значение,
 * нормализованное сервером к прежнему (обрезанный пробел), не держит форму грязной.
 */
export const useArticleForm = (seed: ArticleForm, seedReadAt: number | undefined) => {
  const [form, setForm] = useState<ArticleForm>(seed)
  const seedRef = useRef(seed)
  const sentRef = useRef<{ base: ArticleForm; savedAt: number } | null>(null)
  // Ушедшая форма, пока не приехал засев после сохранения: совпадает с ней — уже
  // «Сохранено». Без этого после крутилки на секунду возвращалось «Сохранить».
  const [sentForm, setSentForm] = useState<ArticleForm | null>(null)
  useEffect(() => {
    const prev = seedRef.current
    const sent = sentRef.current
    const final = sent !== null && seedReadAt !== undefined && seedReadAt >= sent.savedAt
    if (!final && (prev === seed || sameValues(prev, seed))) return
    seedRef.current = seed
    sentRef.current = null
    setSentForm(null)
    setForm((f) => rebaseArticleForm(f, sent?.base ?? prev, seed))
  }, [seed, seedReadAt])

  // `timeToRead` в базу идёт значением формы (форма его не правит), поэтому ключ остаётся
  // «нетронутым» и берётся из нового засева.
  const markSent = (sentForm: ArticleForm, { patch, savedAt }: SavedPatch) => {
    const base: ArticleForm = { ...seedRef.current }
    for (const key of Object.keys(patch) as (keyof typeof patch & keyof ArticleForm)[])
      Object.assign(base, { [key]: sentForm[key] })
    sentRef.current = { base, savedAt }
    setSentForm(sentForm)
  }
  const update = <K extends keyof ArticleForm>(key: K, val: ArticleForm[K]) =>
    setForm((f) => ({ ...f, [key]: val }))
  const dirty = !sameValues(form, seed) && !(sentForm !== null && sameValues(form, sentForm))
  return { form, update, markSent, dirty }
}
